/**
 * Utility functions for safe ID comparison and validation
 * 
 * These functions provide robust ID comparison and validation that handles
 * different data types, null/undefined values, and edge cases commonly
 * encountered in web applications.
 * 
 * @module idComparison
 */

/**
 * Safely compares two IDs, handling different types and null/undefined values
 * 
 * This function provides a robust comparison that:
 * - Handles null/undefined values gracefully
 * - Converts different data types to strings for comparison
 * - Trims whitespace from string values
 * - Treats empty strings as invalid (not equal)
 * - Is case-sensitive for string comparisons
 * - Includes error handling for unexpected conversion issues
 * 
 * @param id1 - First ID to compare (can be string, number, null, or undefined)
 * @param id2 - Second ID to compare (can be string, number, null, or undefined)
 * @returns true if IDs are equal and valid, false otherwise
 * 
 * @example
 * ```typescript
 * safeIdComparison('user-123', 'user-123') // true
 * safeIdComparison('user-123', 'user-456') // false
 * safeIdComparison('user-123', null) // false
 * safeIdComparison('  user-123  ', 'user-123') // true (whitespace trimmed)
 * safeIdComparison('', '') // false (empty strings not considered equal)
 * ```
 */
export const safeIdComparison = (id1: string | number | null | undefined, id2: string | number | null | undefined): boolean => {
  // Handle null/undefined cases
  if (id1 === null || id1 === undefined || id2 === null || id2 === undefined) {
    return false;
  }
  
  try {
    // Convert both to strings for comparison
    const str1 = String(id1).trim();
    const str2 = String(id2).trim();
    
    // Empty strings are not considered equal
    if (str1 === '' || str2 === '') {
      return false;
    }
    
    return str1 === str2;
  } catch (error) {
    // Handle any unexpected conversion errors gracefully
    return false;
  }
};

/**
 * Validates if a given ID is in a valid format
 * 
 * This function checks if an ID:
 * - Is not null or undefined
 * - Converts to a non-empty string after trimming
 * - Is suitable for use in comparisons or database queries
 * 
 * @param id - ID to validate (can be string, number, null, or undefined)
 * @returns true if ID is valid, false otherwise
 * 
 * @example
 * ```typescript
 * isValidId('user-123') // true
 * isValidId(123) // true
 * isValidId('') // false
 * isValidId('  ') // false
 * isValidId(null) // false
 * isValidId(undefined) // false
 * ```
 */
export const isValidId = (id: string | number | null | undefined): boolean => {
  if (id === null || id === undefined) {
    return false;
  }
  
  const str = String(id).trim();
  return str.length > 0;
};
